import { z } from 'zod';
import { EVAL_ATTACHMENT_HASH_REGEX } from './evals';

// ---------------------------------------------------------------------------
// A voice call's OPENING (realtime voice calls, gh #4418): the audio the caller's app plays
// before the voice connects, with the caller's microphone muted — the AI disclosure. The
// platform plays its own clip in the caller's app language (`apps/web/public/audio/
// call-disclosure/`). A hub's `pilot_voice` agent may replace it for a language with an audio
// file of its own and a caption the call bar shows while it plays (`agent.call_openings`). A
// language it does not replace plays the platform's clip: a call always has an opening.
//
// The bytes are content-addressed in the resource bucket (`buildCallOpeningR2Key`:
// `{hub_id}/call-openings/{sha256}`), so an opening's reference copies from preview to
// production verbatim and publish copies its bytes alongside (`collectCallOpeningCopies`).
// ---------------------------------------------------------------------------

/**
 * The fixed segment 1 of every call opening's key in the resource bucket
 * (`{hub_id}/call-openings/{sha256}`): the runtime's key builder and the editor's playback
 * path both take it from here.
 */
export const CALL_OPENINGS_SEGMENT = 'call-openings';

/** The languages a call opens in: the caller's app languages. */
export const CALL_OPENING_LANGUAGES = ['en', 'pt', 'es'] as const;
export const callOpeningLanguage = z.enum(CALL_OPENING_LANGUAGES);
export type CallOpeningLanguage = z.infer<typeof callOpeningLanguage>;

/**
 * The longest opening a hub may upload. The call's ready window (`READY_TIMEOUT_MS`, 30 s from
 * the create, in `call-control-do.ts`) closes unless `ready` arrives in it, and `ready` waits
 * for the opening to finish. The opening starts playing before the create (the offer and ICE
 * gathering run while it plays), so an opening of this length ends at most 15 s into the
 * window, whatever the setup took, and leaves the other half for the media connection and the
 * `ready` request.
 */
export const CALL_OPENING_MAX_DURATION_MS = 15_000;

/**
 * The largest opening a hub may upload, in bytes. The caller's app loads the whole opening
 * before the call is created, within its 10 s load bound (`DISCLOSURE_LOAD_TIMEOUT_MS` in the
 * web client's `disclosure.ts`), so the bytes take nothing from the ready window: this bound
 * is what keeps that load inside 10 s on a slow connection (1 MiB needs about 0.84 Mbit/s).
 * Fifteen seconds of MP3 at its highest bitrate (320 kbps) is 600 KB, so no honest opening
 * comes near it.
 */
export const CALL_OPENING_MAX_BYTES = 1024 * 1024;

/** The longest caption: what the call bar shows while an opening plays (15 s of speech is ~230). */
export const CALL_OPENING_CAPTION_MAX_CHARS = 500;

/** The audio containers an opening may be: MP3, or AAC in MP4. Told by the bytes at upload. */
export const CALL_OPENING_CONTENT_TYPES = ['audio/mpeg', 'audio/mp4'] as const;
export const callOpeningContentType = z.enum(CALL_OPENING_CONTENT_TYPES);

/** An opening's content address: the sha256 of its bytes, lowercase hex — the eval attachments' own. */
export const CALL_OPENING_HASH_REGEX = EVAL_ATTACHMENT_HASH_REGEX;
export const callOpeningHash = z.string().regex(CALL_OPENING_HASH_REGEX, 'Expected a sha256 hex digest');

export const callOpeningCaption = z.string().trim().min(1).max(CALL_OPENING_CAPTION_MAX_CHARS);

/**
 * One language's opening as the agent row stores it (`agent.call_openings[language]`). The
 * content type, length and size are read from the bytes at upload, never taken from a client.
 */
export const storedCallOpening = z.object({
  hash: callOpeningHash,
  content_type: callOpeningContentType,
  duration_ms: z.number().int().positive().max(CALL_OPENING_MAX_DURATION_MS),
  file_size: z.number().int().positive().max(CALL_OPENING_MAX_BYTES),
  caption: callOpeningCaption,
});
export type StoredCallOpening = z.infer<typeof storedCallOpening>;

/** A voice agent's openings, by language; a language it leaves out plays the platform's. */
export type StoredCallOpenings = Partial<Record<CallOpeningLanguage, StoredCallOpening>>;

/**
 * A voice agent's openings as stored (`agent.call_openings`: a parsed object, or its JSON text),
 * by language: only the entries that parse as a stored opening. A language left out, or stored
 * malformed, plays the platform's opening. Every reader goes through this — the callers' signal
 * and the opening route over the voice agent a call runs on, the writer's merge, publish's byte
 * copies, and hub-as-code — so none can accept an entry another refuses.
 */
export function readStoredCallOpenings(raw: unknown): StoredCallOpenings {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  const openings: StoredCallOpenings = {};
  for (const language of CALL_OPENING_LANGUAGES) {
    const parsed = storedCallOpening.safeParse((value as Record<string, unknown>)[language]);
    if (parsed.success) openings[language] = parsed.data;
  }
  return openings;
}

/**
 * The most base64 characters an opening's upload may carry: the byte bound, encoded (4 per 3),
 * plus padding slack. Checked before decoding, so an oversized body is refused unallocated.
 */
export const MAX_CALL_OPENING_ENCODED_BYTES = Math.ceil(CALL_OPENING_MAX_BYTES / 3) * 4 + 16;

/** The raw body bound on the upload route: the encoded bytes, the caption, and the envelope. */
export const MAX_CALL_OPENING_BODY_BYTES = MAX_CALL_OPENING_ENCODED_BYTES + 16 * 1024;

// ---------------------------------------------------------------------------
// PUT    /setup/agents/:id/call-openings/:language — set a language's opening (preview hubs)
// DELETE /setup/agents/:id/call-openings/:language — back to the platform's opening
// ---------------------------------------------------------------------------

export const agentCallOpeningParams = z.object({
  id: z.string().uuid(),
  language: callOpeningLanguage,
});
export type AgentCallOpeningParams = z.infer<typeof agentCallOpeningParams>;

export const putAgentCallOpeningBody = z.object({
  hub_id: z.string().uuid(),
  caption: callOpeningCaption,
  /**
   * The audio, base64. Absent: the language's current audio stays and only the caption
   * changes, which needs an opening already set for the language.
   */
  file_data: z.string().min(1).max(MAX_CALL_OPENING_ENCODED_BYTES).optional(),
});
export type PutAgentCallOpeningBody = z.infer<typeof putAgentCallOpeningBody>;

export const deleteAgentCallOpeningQuery = z.object({
  hub_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// GET /calls/openings/:language — the opening of the hub being called, for its caller
// ---------------------------------------------------------------------------

export const callOpeningParam = z.object({ language: callOpeningLanguage });

/** 32-bit FNV-1a of a string's UTF-16 code units, as 8 hex digits: stable in every JS runtime. */
function fnv1a32Hex(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * The version a caller's app is told an opening by, and asks for it by: its audio's content
 * hash and a digest of its caption, so an opening whose caption alone changed is a new version
 * too. The opening route serves only the version it would tell now.
 */
export function callOpeningVersion(opening: Pick<StoredCallOpening, 'hash' | 'caption'>): string {
  return `${opening.hash}.${fnv1a32Hex(opening.caption)}`;
}

export const callOpeningQuery = z.object({
  hub_id: z.string().uuid(),
  /**
   * The opening the caller's app was told of (`HubCallOpening.version`, `callOpeningVersion`).
   * Any other answers 404 — the audio or the caption changed since — and the app plays the
   * platform's opening, so a caption never shows over audio it does not describe.
   */
  v: z.string().regex(/^[a-f0-9]{64}\.[a-f0-9]{8}$/, 'Expected a call opening version'),
});
export type CallOpeningQuery = z.infer<typeof callOpeningQuery>;

/**
 * One language a hub's calls open with its own audio, as its callers' apps are told
 * (`HubItemResponse.voice_call_openings`): the caption to show while it plays, and the version
 * to fetch it by (`callOpeningVersion`). A language not listed plays the platform's opening.
 */
export interface HubCallOpening {
  language: CallOpeningLanguage;
  caption: string;
  version: string;
}
